import { Box } from "@mui/material";
import Grid from "@mui/material/Grid2";
import React from "react";
import { Trans } from "react-i18next";
import { DotLegalButton, DotLegalHeader, DotLegalPageHeader, DotLegalPrettyScrollbar } from "@dotlegal/dotlegal-ui-components";
import DetailsWrapper from "./detailsWrapper/DetailsWrapper";
import DotLegalFullScreenSpinner from "../common/components/dotLegalFullScreenSpinner/DotLegalFullScreenSpinner";
import DotLegalLink from "../common/components/dotLegalLink/DotLegalLink";
import DotLegalNotificationCard from "../common/components/dotLegalNotificationCard/DotLegalNotificationCard";
import DotLegalPaper from "../common/components/dotLegalPaper/DotLegalPaper";
import { useTranslation } from "../localization/useTranslation";
import DeleteNotificationDialog from "./deleteNotificationDialog/DeleteNotificationDialog";
import { useNotificationDataMapping } from "./Notification.hooks";
import { useNotificationStyles } from "./Notification.styles";
import { getNotificationDescriptionExpanded, getNotificationHeader, NotificationType, NotificationViewModel } from "./Notification.types";
import NotificationEmptyState from "./notificationEmptyState/NotificationEmptyState";
import NotificationMarkDownContent from "./notificationMarkDownContent/NotificationMarkDownContent";
import { useUrlProvider } from "../useUrlProvider";

function Notification() {
    const { translateString, translateDate, translateDateWithFullMonthAndYear } = useTranslation();
    const {
        getProcessingActivityOverviewWithOpenCommenDialogUrl,
        getProcessingActivityOverviewUrl,
        getTaskManagementTaskDialogUrl,
        getTaskManagementUrlForTask,
        getProcessingActivityValidationUrl,
    } = useUrlProvider();
    const {
        isLoading,
        getBreadCrumbs,
        data,
        selectedNotificationId,
        setSelectedNotificationId,
        selectedNotification,
        updateNotificationState,
        showDeleteNotificationDialog,
        setShowDeleteNotificationDialog,
        refetchNotifications,
        hasNotifications,
    } = useNotificationDataMapping();
    const styles = useNotificationStyles();

    const scrollRef = React.createRef<HTMLDivElement>();

    const notificationCardSize = "expanded";
    const getNotifications = () => {
        return data?.map((x) => {
            let header = getNotificationHeader(x.notificationType, translateString);
            if (x.isProcessingActivityValidation) {
                header = translateString("taskNotifications");
            }

            return (
                <Box sx={styles.notificationCard} key={x.id}>
                    <DotLegalNotificationCard
                        onClick={() => {
                            setSelectedNotificationId(x.id);

                            if (!x.isRead) {
                                updateNotificationState(x.id);
                            }
                        }}
                        isClicked={selectedNotificationId === x.id}
                        description={getNotificationDescriptionExpanded(x, translateString)}
                        header={header}
                        isRead={x.isRead}
                        taskHrefText=""
                        taskHref=""
                        taskDate={x.created}
                        onDelete={() => setShowDeleteNotificationDialog(true)}
                        notificationType={x.notificationType}
                        size={notificationCardSize}
                        clickable
                    />
                </Box>
            );
        });
    };

    const getNotificationContent = (notification: NotificationViewModel) => {
        if (notification.content) {
            return <Box dangerouslySetInnerHTML={{ __html: notification.content }} />;
        } else if (notification.contentUrl) {
            return <NotificationMarkDownContent url={notification.contentUrl} />;
        } else if (notification.notificationType === NotificationType.ProcessingActivityCommentType) {
            return getProcessingActivityCommentContent(notification);
        } else if (notification.notificationType === NotificationType.TaskComment) {
            return getTaskCommentContent(notification);
        } else if (notification.notificationType === NotificationType.TaskAssignee || notification.notificationType === NotificationType.TaskResponsible) {
            return getTaskAccountableContent(notification);
        } else if (
            notification.notificationType === NotificationType.TaskCompleted ||
            notification.notificationType === NotificationType.TaskReadyForApproval
        ) {
            return getTaskCompletedContent(notification);
        } else if (notification.notificationType === NotificationType.TaskReminder) {
            return getTaskReminderContent(notification);
        } else if (notification.notificationType === NotificationType.RiskObservationConverted) {
            return getRiskObservationConvertedContent();
        } else {
            return getTaskContent(notification);
        }
    };

    function getProcessingActivityCommentContent(notification: NotificationViewModel) {
        return (
            <React.Fragment>
                <Box sx={{ marginBottom: 1 }}>
                    <Trans
                        i18nKey="notificationNewCommentFullContent"
                        values={{
                            name: notification.sender,
                            processingActivity: notification.processingActivityName,
                        }}
                        components={{
                            url: (
                                <DotLegalLink to={getProcessingActivityOverviewUrl(notification.processingActivityId!)}>
                                    {notification.processingActivityName}
                                </DotLegalLink>
                            ),
                            wrapper: (
                                <Box
                                    sx={(theme) => ({
                                        color: theme.palette.secondary.main,
                                        fontWeight: theme.typography.fontWeightBold,
                                    })}
                                    component="span"
                                ></Box>
                            ),
                        }}
                    />
                </Box>
                <DotLegalHeader fontWeight={0} headerStyle="small">
                    {translateString("notificationNewCommentUnsubscribe")}
                </DotLegalHeader>

                <DotLegalButton href={getProcessingActivityOverviewWithOpenCommenDialogUrl(notification.processingActivityId!)} buttonType="primary">
                    {translateString("nofificationGoToComment")}
                </DotLegalButton>
            </React.Fragment>
        );
    }

    function getTaskCommentContent(notification: NotificationViewModel) {
        return (
            <React.Fragment>
                <Box sx={{ marginBottom: 1 }}>
                    <Trans
                        i18nKey="notificationNewTaskCommentFullContent"
                        values={{
                            name: notification.sender,
                            task: notification.description,
                        }}
                        components={{
                            url: <DotLegalLink to={getTaskManagementTaskDialogUrl(notification.taskId)}>{notification.description}</DotLegalLink>,
                            wrapper: (
                                <Box
                                    sx={(theme) => ({
                                        color: theme.palette.secondary.main,
                                        fontWeight: theme.typography.fontWeightBold,
                                    })}
                                    component="span"
                                ></Box>
                            ),
                        }}
                    />
                </Box>
                <DotLegalHeader fontWeight={0} headerStyle="small">
                    {translateString("notificationTaskCommentUnsubscribe")}
                </DotLegalHeader>

                <DotLegalButton href={getTaskManagementTaskDialogUrl(notification.taskId)} buttonType="primary">
                    {translateString("nofificationGoToComment")}
                </DotLegalButton>
            </React.Fragment>
        );
    }

    function getTaskContent(notification: NotificationViewModel) {
        if (notification.isProcessingActivityValidation) {
            return getProcessingActivityTaskContent(notification);
        }

        return getRegularTaskContent(notification);
    }

    function getProcessingActivityTaskContent(notification: NotificationViewModel) {
        return (
            <React.Fragment>
                <Box sx={styles.content}>
                    <div>
                        {
                            <Trans
                                i18nKey="processingActivityNotification"
                                values={{
                                    days: notification.notificationPeriodInDays,
                                    deadline: translateDate(notification.deadLine!),
                                }}
                            />
                        }
                    </div>
                    <Box sx={styles.section}>{translateString("processingActivitiesToBeValidated")}</Box>
                    <Box sx={styles.processingActivityContent}>
                        {notification.processingActivityTaskViewModel.map((x) => {
                            return (
                                <div key={x.processingActivityId}>
                                    <DotLegalLink to={getProcessingActivityValidationUrl(x.processingActivityId)}>{x.processingActivityName}</DotLegalLink>
                                </div>
                            );
                        })}
                    </Box>
                </Box>

                {getTaskButton(notification)}
            </React.Fragment>
        );
    }

    function getTaskAccountableContent(notification: NotificationViewModel) {
        return (
            <React.Fragment>
                <Box sx={styles.content}>
                    <div>
                        {
                            <Trans
                                i18nKey={
                                    notification.notificationType === NotificationType.TaskAssignee
                                        ? "assigneeNotificationFullDescription"
                                        : "taskResponsibleNotificationFullDescription"
                                }
                                values={{
                                    deadline: translateDate(notification.deadLine!),
                                    task: notification.description,
                                }}
                                components={{
                                    url: <DotLegalLink to={getTaskManagementTaskDialogUrl(notification.taskId)}>{notification.description}</DotLegalLink>,
                                    wrapper: (
                                        <Box
                                            sx={(theme) => ({
                                                color: theme.palette.secondary.main,
                                                fontWeight: theme.typography.fontWeightBold,
                                            })}
                                            component="span"
                                        ></Box>
                                    ),
                                }}
                            />
                        }
                    </div>
                </Box>

                {getTaskButton(notification)}
            </React.Fragment>
        );
    }

    function getTaskReminderContent(notification: NotificationViewModel) {
        return getSingleTaskContent(notification, "taskReminderNotificationFullDescription", {
            fromUser: notification.fromUser,
            task: notification.description,
            deadline: translateDate(notification.deadLine!),
        });
    }

    function getRegularTaskContent(notification: NotificationViewModel) {
        return getSingleTaskContent(notification, "regularTaskNotification", {
            days: notification.notificationPeriodInDays,
            deadline: translateDate(notification.deadLine!),
            type: notification.description,
        });
    }

    function getSingleTaskContent(notification: NotificationViewModel, i18nKey: string, values: Record<string, unknown>) {
        return (
            <React.Fragment>
                <Box sx={styles.content}>
                    <div>
                        <Trans i18nKey={i18nKey} values={values} />
                    </div>
                </Box>

                {getTaskButton(notification)}
            </React.Fragment>
        );
    }

    function getTaskCompletedContent(notification: NotificationViewModel) {
        return (
            <React.Fragment>
                <Box sx={styles.content}>
                    <Trans
                        i18nKey={
                            notification.notificationType === NotificationType.TaskReadyForApproval
                                ? "notificationBodyForTaskReadyForApproval"
                                : "notificationBodyForTaskCompleted"
                        }
                        values={{
                            fromUser: notification.fromUser,
                            date: translateDate(notification.created),
                            deadline: translateDateWithFullMonthAndYear(notification.deadLine!),
                            task: notification.description,
                        }}
                        components={{
                            url: <DotLegalLink to={`${getTaskManagementTaskDialogUrl(notification.taskId)}`}>{notification.description}</DotLegalLink>,
                            wrapper: (
                                <Box
                                    sx={(theme) => ({
                                        color: theme.palette.secondary.main,
                                        fontWeight: theme.typography.fontWeightBold,
                                    })}
                                    component="span"
                                ></Box>
                            ),
                        }}
                    />
                </Box>

                {getTaskButton(notification)}
            </React.Fragment>
        );
    }

    function getRiskObservationConvertedContent() {
        return (
            <Box sx={styles.content}>
                <div>{translateString("riskObservationConvertedNotificationDescription")}</div>
            </Box>
        );
    }

    function getTaskButton(notification: NotificationViewModel) {
        const month = new Date(notification.deadLine!).getMonth() + 1;
        const showAsMultipleTasks = notification.isProcessingActivityValidation;
        let href = getTaskManagementTaskDialogUrl(notification.taskId, month);

        if (showAsMultipleTasks && notification.annualWheelActivityId) {
            href = getTaskManagementUrlForTask(month, notification.annualWheelActivityId);
        }

        return (
            <Box sx={styles.buttons}>
                <DotLegalButton buttonType="secondary" href={href}>
                    {showAsMultipleTasks ? translateString("seeTasks") : translateString("seeTask")}
                </DotLegalButton>
            </Box>
        );
    }

    return (
        <React.Fragment>
            <DotLegalPageHeader breadCrumbs={getBreadCrumbs()} />
            {isLoading ? (
                <DotLegalFullScreenSpinner />
            ) : hasNotifications ? (
                <React.Fragment>
                    <Grid sx={[styles.root, { paddingBottom: 2 }]} container spacing={2}>
                        <Grid style={{ height: "100%" }} size={{ lg: 4, md: 6, sm: 12 }}>
                            <DotLegalPaper fullHeight>
                                <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
                                    <DotLegalPrettyScrollbar width={14} sx={styles.notificationList}>
                                        {getNotifications()}
                                    </DotLegalPrettyScrollbar>
                                </Box>
                            </DotLegalPaper>
                        </Grid>
                        <Grid style={{ height: "100%" }} size={{ lg: 8, md: 6, sm: 12 }}>
                            <DotLegalPaper fullHeight>
                                {selectedNotification ? (
                                    <DetailsWrapper
                                        ref={scrollRef}
                                        notificationType={selectedNotification.notificationType!}
                                        description={selectedNotification.description}
                                        isProcessingActivityValidation={selectedNotification.isProcessingActivityValidation}
                                        date={selectedNotification.created}
                                    >
                                        {getNotificationContent(selectedNotification)}
                                    </DetailsWrapper>
                                ) : (
                                    <NotificationEmptyState notificationText={translateString("noNotificationsSelected")} isPopOver={false} />
                                )}
                            </DotLegalPaper>
                        </Grid>
                    </Grid>
                </React.Fragment>
            ) : (
                <DotLegalPaper fullHeight>
                    <NotificationEmptyState notificationText={translateString("noNotifications")} isPopOver={false} />
                </DotLegalPaper>
            )}
            {showDeleteNotificationDialog && (
                <DeleteNotificationDialog
                    onCloseDialog={() => setShowDeleteNotificationDialog(false)}
                    onSaveCorrect={() => refetchNotifications()}
                    notification={selectedNotification!}
                />
            )}
        </React.Fragment>
    );
}

export default Notification;
